import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { GuessedMapping } from '@/lib/price-list-break-pivot/types';

type MappingRole = keyof GuessedMapping;

function MappingSelect({
  id,
  label,
  ariaLabel,
  value,
  headers,
  onChange,
  disabled,
}: {
  id: string;
  label: ReactNode;
  ariaLabel: string;
  value: string | null;
  headers: readonly string[];
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Select
        value={value ?? undefined}
        onValueChange={onChange}
        disabled={disabled || headers.length === 0}
      >
        <SelectTrigger id={id} className="h-8" aria-label={ariaLabel}>
          <SelectValue
            placeholder={gt('Select a column', {
              context:
                'placeholder when a price-list pivot mapping dropdown has no column yet',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          {headers.map((header) => (
            <SelectItem key={header} value={header}>
              {header}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function MappingControls({
  headers,
  mapping,
  disabled,
  onChange,
}: {
  headers: readonly string[];
  mapping: GuessedMapping;
  disabled: boolean;
  onChange: (next: GuessedMapping) => void;
}) {
  const gt = useGT();

  function setRole(role: MappingRole, value: string): void {
    onChange({ ...mapping, [role]: value });
  }

  if (headers.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <T context="heading for price-list break pivot column mapping">
            Column mapping
          </T>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          <T context="help text for price-list break pivot column mapping">
            Key is the item identity. Break is the quantity or price bucket.
            Price is the value that becomes one column per break.
          </T>
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 sm:flex-row">
        <MappingSelect
          id="pivot-key-column"
          label={
            <T context="label for the identity column dropdown">Key column</T>
          }
          ariaLabel={gt('Key column', {
            context: 'aria-label for the identity column dropdown',
          })}
          value={mapping.keyColumn}
          headers={headers}
          disabled={disabled}
          onChange={(value) => setRole('keyColumn', value)}
        />
        <MappingSelect
          id="pivot-break-column"
          label={
            <T context="label for the quantity-break column dropdown">
              Break column
            </T>
          }
          ariaLabel={gt('Break column', {
            context: 'aria-label for the quantity-break column dropdown',
          })}
          value={mapping.breakColumn}
          headers={headers}
          disabled={disabled}
          onChange={(value) => setRole('breakColumn', value)}
        />
        <MappingSelect
          id="pivot-price-column"
          label={
            <T context="label for the price value column dropdown">
              Price column
            </T>
          }
          ariaLabel={gt('Price column', {
            context: 'aria-label for the price value column dropdown',
          })}
          value={mapping.priceColumn}
          headers={headers}
          disabled={disabled}
          onChange={(value) => setRole('priceColumn', value)}
        />
      </CardContent>
    </Card>
  );
}
